import { withDefaultEntityParents } from '../components/workspace/dynamicFormFieldUtils';
import type {
  FormFieldDataType,
  FormFieldDefinition,
  FormFieldOption,
  FormFieldType,
  FormSchema,
  FormSection,
} from '../types/formBuilder';

type RawOption = string | {
  value?: string;
  label?: string;
  isDefault?: boolean;
  sortOrder?: number;
  enabled?: boolean;
};

type RawField = {
  fieldId?: string;
  internalName?: string;
  fieldName?: string;
  label?: string;
  fieldType?: string;
  dataType?: string;
  mandatory?: boolean;
  isRequired?: boolean;
  helpText?: string;
  placeholder?: string;
  defaultValue?: string | number | boolean | null;
  sortOrder?: number;
  displayInList?: boolean;
  isDisplayInList?: boolean;
  options?: RawOption[];
  sectionId?: string;
  properties?: Record<string, unknown>;
  entitySelectorConfig?: Record<string, unknown>;
};

type RawSection = {
  sectionId?: string;
  title?: string;
  sectionName?: string;
  description?: string;
  sortOrder?: number;
  sectionOrder?: number;
  fields?: RawField[];
};

type RawSchema = Partial<FormSchema> & {
  formName?: string;
  formCode?: string;
  sections?: RawSection[];
  fields?: RawField[];
};

function inferDataType(fieldType: FormFieldType): FormFieldDataType {
  switch (fieldType) {
    case 'NUMBER':
      return 'DECIMAL';
    case 'CHECKBOX':
    case 'TERMS_AND_CONDITIONS':
      return 'BOOLEAN';
    case 'MULTISELECT':
    case 'REPEATABLE_GROUP':
      return 'ARRAY';
    case 'DATE':
      return 'DATETIME';
    case 'FILE':
    case 'IMAGE':
      return 'FILE';
    case 'RICHTEXT':
    case 'DISPLAY':
      return 'HTML';
    default:
      return 'STRING';
  }
}

function normalizeFieldType(fieldType?: string): FormFieldType {
  const normalized = (fieldType ?? 'TEXT').toUpperCase() as FormFieldType;
  return normalized;
}

function normalizeOptions(options?: RawOption[]): FormFieldOption[] | undefined {
  if (!options?.length) {
    return undefined;
  }

  return options.map((option, index) => {
    if (typeof option === 'string') {
      return {
        value: option,
        label: option,
        isDefault: false,
        sortOrder: index,
        enabled: true,
      };
    }

    const value = option.value ?? option.label ?? '';
    return {
      value,
      label: option.label ?? value,
      isDefault: option.isDefault ?? false,
      sortOrder: option.sortOrder ?? index,
      enabled: option.enabled ?? true,
    };
  });
}

function normalizeEntitySelectorType(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toUpperCase();
  return normalized || undefined;
}

function normalizeEntitySelectorConfig(field: RawField): FormFieldDefinition['entitySelectorConfig'] | undefined {
  const direct = (field.entitySelectorConfig && typeof field.entitySelectorConfig === 'object')
    ? field.entitySelectorConfig
    : undefined;
  const properties = field.properties && typeof field.properties === 'object'
    ? field.properties
    : undefined;
  const nestedFromProperties = properties?.entitySelectorConfig && typeof properties.entitySelectorConfig === 'object'
    ? (properties.entitySelectorConfig as Record<string, unknown>)
    : undefined;

  const source = direct ?? nestedFromProperties;
  const sourceEntityType = normalizeEntitySelectorType(source?.entityType ?? properties?.entityType);

  if (!sourceEntityType) {
    return undefined;
  }

  return {
    entityType: sourceEntityType as NonNullable<FormFieldDefinition['entitySelectorConfig']>['entityType'],
    parentDependencyFieldId: typeof source?.parentDependencyFieldId === 'string'
      ? source.parentDependencyFieldId
      : undefined,
    multiSelect: Boolean(source?.multiSelect),
    searchEnabled: source?.searchEnabled !== false,
    lazyLoading: source?.lazyLoading !== false,
    autoMappingEnabled: Boolean(source?.autoMappingEnabled),
    hierarchyEnforcement: source?.hierarchyEnforcement !== false,
    autoMappedFields: Array.isArray(source?.autoMappedFields)
      ? source.autoMappedFields.filter((item): item is string => typeof item === 'string')
      : undefined,
    filterRules: Array.isArray(source?.filterRules)
      ? source.filterRules as NonNullable<FormFieldDefinition['entitySelectorConfig']>['filterRules']
      : undefined,
  };
}

function normalizeField(field: RawField, index: number, sectionId?: string): FormFieldDefinition {
  const passthrough = field as unknown as Partial<FormFieldDefinition>;
  const fieldType = normalizeFieldType(field.fieldType);
  const internalName = field.internalName ?? field.fieldId ?? `field_${index + 1}`;
  const label = field.label ?? field.fieldName ?? internalName;
  const defaultValue = field.defaultValue;
  const normalizedEntitySelectorConfig = normalizeEntitySelectorConfig(field);

  return {
    ...passthrough,
    fieldId: field.fieldId ?? internalName,
    label,
    internalName,
    fieldType,
    dataType: (field.dataType as FormFieldDataType | undefined) ?? inferDataType(fieldType),
    mandatory: field.mandatory ?? field.isRequired ?? false,
    helpText: field.helpText,
    placeholder: field.placeholder,
    defaultValue:
      defaultValue === null || defaultValue === undefined
        ? undefined
        : String(defaultValue),
    sortOrder: field.sortOrder ?? index,
    displayInList: field.displayInList ?? field.isDisplayInList,
    sectionId: field.sectionId ?? sectionId,
    options: normalizeOptions(field.options),
    entitySelectorConfig: normalizedEntitySelectorConfig ?? passthrough.entitySelectorConfig,
  };
}

function normalizeSections(sections?: RawSection[]): FormSection[] {
  if (!sections?.length) {
    return [];
  }

  return sections.map((section, index) => ({
    sectionId: section.sectionId ?? `section_${index + 1}`,
    title: section.title ?? section.sectionName ?? `Section ${index + 1}`,
    description: section.description,
    sortOrder: section.sortOrder ?? section.sectionOrder ?? index,
  }));
}

function normalizeSectionFields(sections?: RawSection[]): FormFieldDefinition[] {
  if (!sections?.length) {
    return [];
  }

  return sections.flatMap((section, sectionIndex) =>
    (section.fields ?? []).map((field, fieldIndex) =>
      normalizeField(field, field.sortOrder ?? fieldIndex, section.sectionId ?? `section_${sectionIndex + 1}`),
    ),
  );
}

export function normalizeFormSchema(schema: RawSchema | null | undefined): FormSchema | null {
  if (!schema) {
    return null;
  }

  const sections = normalizeSections(schema.sections);
  const fields = schema.fields?.length
    ? schema.fields.map((field, index) => normalizeField(field as RawField, index))
    : normalizeSectionFields(schema.sections);

  return {
    schemaVersion: schema.schemaVersion ?? '1.0',
    title: schema.title ?? schema.formName ?? 'Untitled Form',
    description: schema.description,
    sections,
    fields: withDefaultEntityParents(fields),
    rules: schema.rules,
    uiVisibilityRules: schema.uiVisibilityRules,
    formValidationRules: schema.formValidationRules,
    styling: schema.styling,
    properties: schema.properties,
  };
}

export function parseAndNormalizeFormSchema(formSchema: string | null | undefined): FormSchema | null {
  if (!formSchema) {
    return null;
  }

  try {
    return normalizeFormSchema(JSON.parse(formSchema) as RawSchema);
  } catch (error) {
    console.error('Failed to parse form schema:', error);
    return null;
  }
}

/** Safely parse a JSON string. Returns null instead of throwing on invalid input. */
export function safeParseJson(value: string): unknown {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}